import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {arraySwapItems, Control, logger, StringControl} from '../../../../../lib/helpers';
import {SelectChoice, SelectItem, SelectQuestionModel} from '../models';
import {ErrorItem, SelectQuestionCrudModel, SelectQuestionCrudParagraph} from './models';
import {selectQuestionCrudHelper} from './select-question-crud-helper';

export class SelectChoiceStore {
  readonly label = new StringControl('', '');
  readonly selected = new Control<boolean>('', false);

  constructor() {
    makeObservable(this);
  }

  @computed
  get model(): SelectChoice {
    return {
      label: this.label.value.trim(),
      selected: this.selected.value
    };
  }

  @action
  sync(choice: SelectChoice) {
    this.label.setValue(choice.label);
    this.selected.setValue(choice.selected);
  }
}

export class SelectStore {
  @observable.shallow
  readonly choices: SelectChoiceStore[] = [];

  constructor() {
    makeObservable(this);
  }

  @computed
  get hasSelectedChoice(): boolean {
    for (let choice of this.choices)
      if (choice.selected.value && choice.label.value.trim() !== '')
        return true;
    return false;
  }

  @computed
  get hasNotSelectedChoice(): boolean {
    for (let choice of this.choices)
      if (!choice.selected.value && choice.label.value.trim() !== '')
        return true;
    return false;
  }

  @computed
  get valid(): boolean {
    return this.hasSelectedChoice && this.hasNotSelectedChoice;
  }

  @computed
  get model(): SelectItem | ErrorItem {
    if (!this.hasSelectedChoice)
      return {type: 'select-error', message: 'No selected(correct) choice'};
    if (!this.hasNotSelectedChoice)
      return {type: 'select-error', message: 'No unselected(incorrect) choice'};
    const choices = this.choices
      .map(x => x.model)
      .filter(x => x.label.trim() !== '');

    return {
      type: 'select',
      choices: choices
    };
  }

  @action
  addChoice() {
    this.choices.push(new SelectChoiceStore());
  }

  @action
  removeChoice(index: number) {
    this.choices.splice(index, 1);
  }

  @action
  selectChoice = (i: number) => {
    for (let choice of this.choices)
      choice.selected.setValue(false);
    this.choices[i].selected.setValue(true);
  };

  @action
  sync = (model: SelectItem) => {
    this.choices.splice(0);
    for (let choice of model.choices) {
      const store = new SelectChoiceStore();
      store.sync(choice);
      this.choices.push(store);
    }
  };
}

export class SelectQuestionEditStore {
  readonly serialized: StringControl;

  @observable.shallow
  readonly selectStores: SelectStore[];

  constructor() {
    makeObservable(this);
    this.serialized = new StringControl('', '');
    this.selectStores = [];
  }

  @action
  setModel = (model: SelectQuestionModel) => {
    this.serialized.setValue(selectQuestionCrudHelper.serializeParagraphs(model.paragraphs));
    this.selectStores.splice(0);
    for (let selectItem of selectQuestionCrudHelper.selectItemsList(model.paragraphs)) {
      const selectStore = new SelectStore();
      selectStore.sync(selectItem);
      this.selectStores.push(selectStore);
    }
  };

  @computed
  get paragraphs(): SelectQuestionCrudParagraph[] {
    return selectQuestionCrudHelper.deserializeParagraphs(
      this.serialized.value,
      this.selectStores.map(x => x.model));
  }

  @computed
  get valid(): boolean {
    const selectCount = this.selectCountFromSerialized;
    if (selectCount > this.selectStores.length)
      return false;
    for (let i = 0; i < selectCount; i++) {
      if (!this.selectStores[i].valid)
        return false;
    }
    return true;
  }

  @computed
  get model(): SelectQuestionCrudModel {
    return {
      paragraphs: this.paragraphs
    };
  }

  @computed
  get x9Content(): string {
    if (!this.valid)
      return '';
    return JSON.stringify(this.model);
  }

  @computed
  get selectCountFromSerialized() {
    return selectQuestionCrudHelper.countSelects(this.serialized.value);
  }

  @computed
  get hasExtraSelectStores() {
    return this.selectStores.length > this.selectCountFromSerialized;
  }

  @computed
  get hasMissingSelectStores() {
    return this.selectStores.length < this.selectCountFromSerialized;
  }

  @action
  swapUp = (index: number) => {
    arraySwapItems(this.selectStores, index, index - 1);
  };

  @action
  swapDown = (index: number) => {
    arraySwapItems(this.selectStores, index, index + 1);
  };

  @action
  addSelectStore = (index: number) => {
    this.selectStores.splice(index, 0, new SelectStore());
  };

  @action
  removeSelectStore = (index: number) => {
    this.selectStores.splice(index, 1);
  };

  dump() {
    reaction(
      () => [...this.selectStores],
      () => logger.log(this.selectStores)
    );
  }
}
